import { Injectable } from '@angular/core';
import { BaseApi } from './_base-api';
import { AssetAuditEntity, DiscoveredPropertyDetails, DiscoveredPropertyEntity, DiscoveredPropertyUpdate, AssetAuditDetails, PropertyCreate, PropertyDetails, PropertyEntity, PropertyHistoryEntity, PropertyUpdate, UnmanagedPropertyEntity, GroupedAssetAuditEntity, UpdatePropertyTag, PlanAssetAuditUpdate, AssetAudit } from '../models';
import { GetApiAdapter, getFieldToUpdateModel } from '../adapters';
import { DataGetFn, DataGetOptions, DataGetRequest } from '@controladad/ng-base';

@Injectable({
  providedIn: 'root'
})
export class PropertyApiService extends BaseApi<PropertyEntity> {
  constructor() {
    super('', {
      get: '/Property/admin/GetPropertyList'
    });
  }

  getProperty(id: number) {
    return http$.get<PropertyDetails>(`/Property/admin/GetProperty?Id=${id}`);
  }

  getAllProperties() {
    return http$.get<PropertyEntity[]>('/Property/admin/GetAllProperties');
  }

  createProperty(model: PropertyCreate) {
    return http$.post('/Property/admin/CreateProperty', model);
  }

  updateProperty(id: number, model: PropertyUpdate) {
    return http$.put('/Property/admin/UpdateProperty', { id, property: getFieldToUpdateModel(model) });
  }

  deleteProperty(id: number) {
    return http$.delete('/Property/admin/DeleteProperty', { body: { id } });
  }

  propertyHistoryAdapter(id: number, options?: DataGetOptions<PropertyHistoryEntity>) {
    return GetApiAdapter<PropertyHistoryEntity>(`/Property/admin/GetPropertyHistory?PropertyId=${id}`, options);
  }

  discoveredPropertiesAdapter(options?: DataGetOptions<DiscoveredPropertyEntity>) {
    return GetApiAdapter<DiscoveredPropertyEntity>(`/Property/admin/GetDiscoveredPropertyList`, options);
  }

  getDiscoveredProperty(id: number) {
    return http$.get<DiscoveredPropertyDetails>(`/Property/admin/GetDiscoveredProperty?Id=${id}`);
  }

  updateDiscoveredProperty(id: number, model: DiscoveredPropertyUpdate) {
    const { personnelIds, ...m } = model;
    return http$.put('/Property/admin/UpdateDiscoveredProperty', { id, personnelIds, ...getFieldToUpdateModel(m) });
  }

  assetAuditsAdapter(options?: DataGetOptions<AssetAuditEntity>) {
    return GetApiAdapter<AssetAuditEntity>(`/Property/admin/GetAllAssetAudits`, options);
  }

  getAssetAuditDetails(id: number) {
    return http$.get<AssetAuditDetails>(`/Property/admin/GetAssetAudit?Id=${id}`);
  }

  exportAssetAudits(query?: DataGetRequest) {
    return super.exportAndDownload('/Property/admin/ExportAllAssetAudits', query, 'asset-audits');
  }

  unmanagedPropertiesAdapter(planId: number, options?: DataGetOptions<UnmanagedPropertyEntity>) {
    return GetApiAdapter<UnmanagedPropertyEntity>(`/Property/admin/GetUnmanagedProperties?Filters.PlanId=${planId}`, options);
  }

  managedPropertiesAdapter(planId: number, options?: DataGetOptions<GroupedAssetAuditEntity>): DataGetFn<AssetAudit> {
    return GetApiAdapter<GroupedAssetAuditEntity>(`/Property/admin/GetPlanAssetAudits?PlanId=${planId}`, {
      ...options, mapFn: (x) => {
        return {
          data: x.data.flatMap((g: GroupedAssetAuditEntity) => [
            { groupBy: g.locationName },
            ...g.assetAudits, 
          ]),
          pagination: x.pagination
        };
      },
    }) as any;
  }

  updatePlanAssetAudit(id: number, model: Partial<PlanAssetAuditUpdate>) {
    return http$.put('/Property/admin/UpdatePlanAssetAudit', { id, audit: getFieldToUpdateModel(model) });
  }

  approveLateAssetAudit(id: number) {
    return http$.post('/Property/admin/ApproveLateAssetAudit', { pendingAssetAuditId: id });
  }

  getPropertyTagsAdapter(options?: DataGetOptions<PropertyEntity>) {
    return GetApiAdapter<PropertyEntity>(`/Property/admin/GetPropertyTagList`, options);
  }

  exportPropertyTags(query?: DataGetRequest) {
    return super.exportAndDownload('/Property/admin/ExportPropertyTags', query, 'property-tags');
  }

  exportDiscoveredProperties(query?: DataGetRequest) {
    return super.exportAndDownload('/Property/admin/ExportDiscoveredProperties', query, 'discovered-propertie');
  }

  bulkUpdatePropertyTags(model: UpdatePropertyTag[]) {
    return http$.put('/Property/admin/BulkUpdatePropertyTags', model);
  }

}